import { useEffect, useRef } from 'react';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import type { DeliveryZone } from '../types';

function ZonePolygonOverlay({ zone }: { zone: DeliveryZone }) {
  const map = useMap();
  const polygonRef = useRef<google.maps.Polygon | null>(null);

  useEffect(() => {
    if (!map || !zone.shape_json || zone.shape_json.length < 3) return;

    if (polygonRef.current) {
      polygonRef.current.setMap(null);
    }

    const poly = new google.maps.Polygon({
      paths: zone.shape_json,
      strokeColor: zone.color || '#FF0000',
      strokeWeight: 4,
      fillColor: zone.color || '#FF0000',
      fillOpacity: 0.15,
      clickable: false,
      map,
    });
    polygonRef.current = poly;

    return () => {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
    };
  }, [map, zone]);

  return null;
}

function FitAllZones({ zones }: { zones: DeliveryZone[] }) {
  const map = useMap();

  useEffect(() => {
    if (!map || zones.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    for (const zone of zones) {
      if (zone.shape_json) {
        for (const p of zone.shape_json) bounds.extend(p);
      }
    }
    map.fitBounds(bounds, 30);
  }, [map, zones]);

  return null;
}

interface ZoneMapProps {
  zones: DeliveryZone[];
  googleApiKey: string;
  /** Explicit height override; omit to let .hc-zone-map-container's
      responsive CSS decide (220px mobile, 400px on wide screens). */
  height?: string;
}

export function ZoneMap({ zones, googleApiKey, height }: ZoneMapProps) {
  const zonesWithPolygon = zones.filter((z) => z.shape_json && z.shape_json.length >= 3);
  if (zonesWithPolygon.length === 0) return null;

  const center = zonesWithPolygon[0].shape_json![0];

  return (
    <div className="hc-zone-map-container" style={height ? { height } : undefined}>
      <APIProvider apiKey={googleApiKey}>
        <Map
          defaultCenter={center}
          defaultZoom={12}
          mapId="info-zone-map"
          style={{ width: '100%', height: '100%' }}
          gestureHandling="cooperative"
          disableDefaultUI
          zoomControl
        >
          {zonesWithPolygon.map((zone) => (
            <ZonePolygonOverlay key={zone.id} zone={zone} />
          ))}
          <FitAllZones zones={zonesWithPolygon} />
        </Map>
      </APIProvider>
    </div>
  );
}
